import React from "react";
import { Outlet, useLoaderData, Await, useRevalidator } from "react-router-dom";
import { useAuthContext } from "@src/context/authContext";
import ManagerContentProvider from "@src/context/managerContext";
import {
  initUserActivityTracker,
  usePageViewTracker,
} from "@src/utils/userActivityTracker";
import { Loader } from "../../components/atoms";

const ManagerSkeleton = () => (
  <div className="flex h-screen justify-center items-center">
    <Loader
      size="xl"
      text="Fetching your most recent data..."
      textColor="black"
    />
  </div>
);

/**
 * Shown when managerData resolves as null (loader ran before user cookie was ready).
 * Triggers a revalidation so the loader re-runs once auth has settled.
 */
const ManagerDataRevalidator = () => {
  const { revalidate, state } = useRevalidator();
  React.useEffect(() => {
    if (state === "idle") {
      revalidate();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <ManagerSkeleton />;
};

export const ManagerContainer = () => {
  const { initialData } = useLoaderData();
  const auth = useAuthContext();

  const shouldTrack =
    auth.impersonatedSessionContext == null && !auth.hasAdminRole;
  initUserActivityTracker(auth, shouldTrack); // Start tracking globally
  usePageViewTracker(auth, shouldTrack); // Track route changes within this component

  return (
    <React.Suspense fallback={<ManagerSkeleton />}>
      <Await resolve={initialData}>
        {(managerData) => {
          if (!managerData) {
            return <ManagerDataRevalidator />;
          }
          return (
            <ManagerContentProvider managerData={managerData}>
              <Outlet />
            </ManagerContentProvider>
          );
        }}
      </Await>
    </React.Suspense>
  );
};
